import { LocationQueryRaw, RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import { DB_TYPE } from '@/common/define/common.define';
import { type DBType } from '@/common/utils/types';
import { i18n } from '@/common/locale';
import { getRtmSelectedInfo } from '@/database/utils/utils';
import { EvMessage } from 'evui';
import { LinkManagerData, useLinkManagerStore } from '@/common/stores/link-manager';
import { storeToRefs } from 'pinia';
import { OVERVIEW_TYPE } from '@/database/utils/overview.define';
import { useDatabaseMetaStore } from '../stores/database-meta';
import { useDatabaseOverviewStore } from '../stores/database-overview';
import { CLUSTER_DIFF_KEY } from './define';
import { LastSelectedInfo } from './overview.types';
import { useSingleStoreClusterInstance } from './overview.uses';

type RTMType = 'multiView' | 'singleView';
type UseRTMDbType = Exclude<DBType, null | 'NONE' | 'java'>;

type UrlQuery = LocationQueryRaw | LinkManagerData['params'];

const DATABASE_LIST_VIEW_PATH = '/database/instance';

const getCurrentViewInfo = (path: string, query: UrlQuery) => {
  const pathArr = path.split('/');
  const [, dbType, view] = pathArr;

  if (!dbType || !view) {
    return null;
  }

  return {
    dbType: dbType as UseRTMDbType,
    viewType: view as RTMType,
    id: query?.id as string | undefined,
  };
};

const hasDbType = {
  multiView: (dbType: UseRTMDbType) => {
    return (
      DB_TYPE.POSTGRESQL === dbType ||
      DB_TYPE.MYSQL === dbType ||
      DB_TYPE.ORACLE === dbType ||
      DB_TYPE.ALTIBASE === dbType ||
      DB_TYPE.SINGLESTORE === dbType
    );
  },
  singleView: (dbType: UseRTMDbType) => {
    return (
      DB_TYPE.POSTGRESQL === dbType || DB_TYPE.MYSQL === dbType || DB_TYPE.SQLSERVER === dbType
    );
  },
  clusterView: (dbType: UseRTMDbType) => {
    return DB_TYPE.SINGLESTORE === dbType;
  },
};

const isSingleView = (viewType?: RTMType) => viewType?.toLowerCase() === 'singleview';

const isMultiView = (viewType?: RTMType) => viewType?.toLowerCase() === 'multiview';

const isClusterView = (viewType?: RTMType) => viewType?.toLowerCase() === 'clusterview';

const isInvalidPath = {
  singleView: (path: string) =>
    path.toLowerCase().includes('singleview') && !path.includes('singleView'),
  multiView: (path: string) =>
    path.toLowerCase().includes('multiview') && !path.includes('multiView'),
  clusterView: (path: string) =>
    path.toLowerCase().includes('clusterView') && !path.includes('clusterView'),
};

const getValidatedPath = (path: string) => {
  if (isInvalidPath.singleView(path)) {
    return path.replace(/singleview/gi, 'singleView');
  }
  if (isInvalidPath.multiView(path)) {
    return path.replace(/multiview/gi, 'multiView');
  }
  if (isInvalidPath.clusterView(path)) {
    return path.replace(/clusterview/gi, 'clusterView');
  }

  return path;
};

const checkIsInvalidId = (
  is: { singleView: boolean; clusterView: boolean; multiView: boolean },
  id?: string,
  dbType?: UseRTMDbType,
): boolean => {
  if (!id) {
    return true;
  }
  if (is.singleView || is.clusterView) {
    const instance = useDatabaseMetaStore().getInstanceById(id) ?? null;
    return !instance || instance.type !== dbType;
  }
  if (is.multiView) {
    const group = useDatabaseMetaStore().getInstanceGroupById(id) ?? null;
    return !group;
  }
  return false;
};

const checkIsValidExternalInboundPath = (menuPath: string) => {
  const isValidOverviewPath = Object.values(OVERVIEW_TYPE).some((type) =>
    menuPath.toLowerCase().includes(type.toLowerCase()),
  );

  return isValidOverviewPath;
};

export const validateDatabaseOverviewRoute = async (
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | undefined> => {
  const linkManagerStore = useLinkManagerStore();
  const { linkManagerData } = storeToRefs(linkManagerStore);
  const { menuPath, params } = linkManagerData.value;

  const overviewStore = useDatabaseOverviewStore();
  const metaStore = useDatabaseMetaStore();
  const { getClusterInstances } = useSingleStoreClusterInstance();

  const urlInfo: { path: string; query: UrlQuery } = {
    path: to.path,
    query: to.query,
  };
  const isUsedExternalInbound = menuPath && checkIsValidExternalInboundPath(menuPath);

  if (isUsedExternalInbound) {
    urlInfo.path = menuPath;
    urlInfo.query = params;
    linkManagerStore.resetLinkManagerData();
  }

  const verifiedPath = getValidatedPath(urlInfo.path);
  const currentInfo = getCurrentViewInfo(verifiedPath, urlInfo.query);

  if (
    !to.path ||
    !currentInfo ||
    !currentInfo.dbType ||
    !hasDbType[currentInfo.viewType]?.(currentInfo.dbType)
  ) {
    return { path: DATABASE_LIST_VIEW_PATH };
  }

  const { viewType, dbType, id } = currentInfo;

  const is = {
    singleView: isSingleView(viewType),
    multiView: isMultiView(viewType),
    clusterView: isClusterView(viewType),
  };

  const isInvalidId = checkIsInvalidId(is, id, dbType);

  if (!id || isInvalidId) {
    await overviewStore.fetchLastOverviewInfo({ dbType, viewType });

    let targetId: string | undefined = overviewStore.getLastSelectedOverviewId({
      dbType,
      viewType,
    });

    if (is.clusterView) {
      const clusterInstances = getClusterInstances();

      const getValidId = () => {
        const isCurIdValid = id && metaStore.getInstanceById(id);
        if (isCurIdValid) {
          return id;
        }

        const isLastIdValid = targetId && metaStore.getInstanceById(targetId);
        if (isLastIdValid) {
          return targetId;
        }

        if (clusterInstances.length > 0) {
          return clusterInstances[0].instanceId;
        }

        return undefined;
      };

      const { instanceId, folderId } = getRtmSelectedInfo({
        idInfo: {
          folderId: getValidId(),
          instanceId: getValidId(),
        },
        folderList: metaStore.getInstanceGroups(),
        dbType,
        hasInstanceId: !!id,
        viewType,
        clusterDiffKey: CLUSTER_DIFF_KEY?.[dbType] || '',
      });

      const lastSelectedInfo = {
        dbType,
        viewType,
        info: {
          folderId,
          instanceId,
        },
      } as LastSelectedInfo;
      await overviewStore.setLastOverviewInfo(lastSelectedInfo);

      targetId = instanceId;
    }

    if (!targetId) {
      EvMessage({
        type: 'error',
        message: i18n.global.t('MESSAGE.INSTANCE_MENU_NOT_FOUND_VALUE', { value: dbType }),
      });

      return { path: DATABASE_LIST_VIEW_PATH };
    }

    return { path: verifiedPath, query: { id: targetId } };
  }

  const instanceId: string | undefined = is.singleView || is.clusterView ? id : undefined;
  const groupId =
    is.singleView || is.clusterView ? metaStore.getInstanceById(id)?.folders?.[0]?.folderId : id;
  const groups = metaStore.getInstanceGroups();
  const { folderId: verifiedGroupId, instanceId: verifiedInstanceId } = getRtmSelectedInfo({
    idInfo: {
      folderId: groupId,
      instanceId,
    },
    folderList: groups,
    dbType,
    hasInstanceId: is.singleView || is.clusterView,
    viewType,
    clusterDiffKey: CLUSTER_DIFF_KEY?.[dbType] || '',
  });

  const lastSelectedInfo = {
    dbType,
    viewType,
    info: {
      folderId: verifiedGroupId ?? '',
      instanceId: verifiedInstanceId ?? '',
    },
  } as LastSelectedInfo;
  await overviewStore.setLastOverviewInfo(lastSelectedInfo);

  if (
    isInvalidPath.singleView(urlInfo.path) ||
    isInvalidPath.multiView(urlInfo.path) ||
    isUsedExternalInbound
  ) {
    return {
      path: verifiedPath,
      query: urlInfo.query as LocationQueryRaw,
    };
  }

  return undefined;
};
